import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useOrganizationContext } from '../agenticsaas/contexts/OrganizationContext';
import { getServiceFrontendIframeBase, getServiceApiUrl } from '../utils/constants';
import {
  EmbedUnavailablePanel,
  useEmbedReachability,
} from '@saas-foundation/shared-frontend/iframeEmbed';

/**
 * On real hostnames each service UI is exposed on its own subdomain UNDER the
 * shell's own hostname — per-customer routing:
 *   shell:    uhc.uhcenters.org
 *   services: <slug>.uhc.uhcenters.org   (e.g. patients.uhc.uhcenters.org)
 * Slugs double as DNS labels — keep them lowercase/hyphenated and in sync
 * with the edge nginx `map $svc $svc_port` block (DevOps/Deployment/nginx).
 */
export function getServicePathForPort(portNum) {
  const portToPath = {
    3001: 'saas-foundation',
    3010: 'edi-platform',
    3050: 'activity-log',
    3051: 'task-manager',
    3060: 'integrations',
    3075: 'agent-studio',
    3076: 'agentic-platform',
    3088: 'synthetic-data',
    3100: 'patients',
    3101: 'practitioners',
    3102: 'claims-core',
    3103: 'medical-codes',
    3104: 'medications',
    3105: 'allergies',
    3106: 'encounters',
    3107: 'clinical-notes',
    3108: 'diagnostic-reports',
    3109: 'payer',
    3110: 'patient-account',
    3120: 'practice-data',
    3052: 'data-importer',
    3057: 'phi-data-transformer',
    3150: 'insurance-verification',
    3151: 'prior-authorization',
    3152: 'charge-capture',
    3153: 'claims-processing',
    3154: 'denial-management',
    3155: 'ar-posting',
    3156: 'contract-management',
    3157: 'remittance-advice',
    3158: 'rcm-compliance',
    3161: 'rcm-agency',
  };
  return portToPath[portNum] || null;
}

/**
 * Base domain that hosts the per-service subdomains. Per-customer deployment
 * model: shell runs on `<customer>.<apex>` (uhc.uhcenters.org) and each
 * service iframe is `<slug>.<customer>.<apex>`. So we anchor to the CURRENT
 * hostname — no `app.` prefix. Overridable via REACT_APP_SERVICES_BASE_DOMAIN
 * for edge cases (e.g. a shell reachable at a bare apex during migration).
 */
export function getServicesBaseDomain(hostname) {
  const explicit = process.env.REACT_APP_SERVICES_BASE_DOMAIN;
  if (explicit && String(explicit).trim()) {
    return String(explicit).trim().replace(/^\.+|\.+$/g, '');
  }
  return String(hostname);
}

/**
 * Build the URL to a sibling service on the correct origin, for cross-service
 * links/iframes in the shell (Analytics, Reporting). Mirrors ServiceFrame:
 *   - localhost / bare IP → http://<host>:<frontendPort>
 *   - real domain         → <protocol>//<slug>.<servicesBaseDomain>
 */
export function buildServiceOrigin(frontendPort) {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const isDomain =
    hostname !== 'localhost' &&
    hostname !== '127.0.0.1' &&
    !hostname.match(/^(\d{1,3}\.){3}\d{1,3}$/);
  const slug = getServicePathForPort(frontendPort);
  if (isDomain && slug) {
    return `${protocol}//${slug}.${getServicesBaseDomain(hostname)}`;
  }
  return `${protocol}//${hostname}:${frontendPort}`;
}

/**
 * ServiceFrame Component
 * Embeds individual service UIs (DenialManagement, ClaimsProcessing, etc.) 
 * within the AgenticRCM shell using iframe
 */
const ServiceFrame = ({ serviceUrl, serviceName, servicePath = '', iframePath = '', chromeless = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const location = useLocation();
  const { organizationId, organizationRcmSettings } = useOrganizationContext();

  const getServiceUrl = useCallback(() => {
    if (!serviceUrl) return null;
    
    // Handle object format with FRONTEND property (e.g., { FRONTEND: 3100, BACKEND: 8100 })
    let port = serviceUrl;
    let pathSuffix = servicePath || iframePath || '';
    if (typeof serviceUrl === 'object' && serviceUrl.FRONTEND !== undefined) {
      port = serviceUrl.FRONTEND;
      pathSuffix = serviceUrl.PATH || serviceUrl.path || pathSuffix;
    }
    
    // Convert to string in case it's a number (port)
    const urlStr = String(port);
    
    // If serviceUrl is already a full URL, use it
    if (urlStr.startsWith('http://') || urlStr.startsWith('https://')) {
      return urlStr;
    }
    
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const portNum = parseInt(urlStr, 10);
    const isDomain =
      hostname !== 'localhost' &&
      hostname !== '127.0.0.1' &&
      !hostname.match(/^(\d{1,3}\.){3}\d{1,3}$/);
    const pathSlug = !Number.isNaN(portNum) ? getServicePathForPort(portNum) : null;

    const overrideBase = getServiceFrontendIframeBase(urlStr);
    let baseUrl;
    if (overrideBase) {
      baseUrl = overrideBase;
    } else if (isDomain && pathSlug) {
      // Each service UI has its own subdomain so root-relative asset paths keep working.
      baseUrl = `${protocol}//${pathSlug}.${getServicesBaseDomain(hostname)}`;
    } else {
      baseUrl = `${protocol}//${hostname}:${urlStr}`;
    }
    
    // Add organization_id and auth token as URL parameters for iframe.
    // Micro-apps on a different port cannot read the shell's localStorage (different origin).
    // IMPORTANT: do not force embedded=1 here; several RCM service UIs treat it as
    // "hide service chrome/sidebar" (used by analytics-style embeds).
    const params = new URLSearchParams();
    if (chromeless || pathSuffix === '/x12-pipeline') {
      params.append('embedded', 'true');
    } else {
      params.append('show_sidebar', '1');
    }
    const orgForIframe =
      organizationId ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId');
    if (orgForIframe) {
      params.append('organization_id', orgForIframe);
    }

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      params.append('auth_token', token);
    }

    if (portNum === 3060) {
      let iframeOrigin = '';
      try {
        iframeOrigin = new URL(baseUrl.split('?')[0]).origin;
      } catch {
        /* ignore */
      }
      if (iframeOrigin) {
        params.append('saas_api_base', iframeOrigin);
      }
    } else {
      try {
        params.append('saas_api_base', window.location.origin);
      } catch {
        /* ignore */
      }
    }

    // Pass org RCM settings so iframe MFEs do not cross-origin fetch SaaS (:3160 from :315x).
    let rcmForIframe = organizationRcmSettings;
    if (!rcmForIframe || typeof rcmForIframe !== 'object') {
      try {
        const raw = sessionStorage.getItem('organization_rcm_settings');
        if (raw) rcmForIframe = JSON.parse(raw);
      } catch {
        /* ignore */
      }
    }
    if (rcmForIframe && typeof rcmForIframe === 'object') {
      try {
        params.append(
          'org_rcm',
          typeof btoa === 'function'
            ? btoa(unescape(encodeURIComponent(JSON.stringify(rcmForIframe))))
            : encodeURIComponent(JSON.stringify(rcmForIframe))
        );
      } catch {
        /* ignore */
      }
    }

    // Contract Management: only pass API base override for localhost or shell proxy embeds.
    // On production subdomains CM resolves same-origin /api/v1 — passing shell:3156 breaks API calls.
    if (portNum === 3156) {
      const shellPort =
        window.location.port ||
        (window.location.protocol === 'https:' ? '443' : '80');
      const shellHost = window.location.hostname;
      const isLocal =
        shellHost === 'localhost' || shellHost === '127.0.0.1';
      const isShellEmbed = shellPort === '3160' || shellPort === '3161';
      if (isLocal || isShellEmbed) {
        try {
          const u = new URL(baseUrl.split('?')[0]);
          let publicBase =
            u.pathname && u.pathname !== '/'
              ? `${u.origin}${u.pathname.replace(/\/$/, '')}`
              : u.origin;
          if (isLocal && !/:3156\b/.test(publicBase)) {
            publicBase = `${window.location.protocol}//${shellHost}:3156`;
          }
          if (publicBase) {
            params.append('contract_api_base', publicBase);
          }
        } catch {
          /* ignore */
        }
      }
    }

    // Claims Processing: iframe on :3153 proxies /api → backend.
    if (portNum === 3153) {
      try {
        const u = new URL(baseUrl.split('?')[0]);
        const publicBase =
          u.pathname && u.pathname !== '/'
            ? `${u.origin}${u.pathname.replace(/\/$/, '')}`
            : u.origin;
        if (publicBase) {
          params.append('claims_api_base', publicBase);
        }
      } catch {
        /* ignore */
      }
    }

    // Charge Capture: iframe on :3152 proxies /api → backend.
    if (portNum === 3152) {
      try {
        const u = new URL(baseUrl.split('?')[0]);
        const publicBase =
          u.pathname && u.pathname !== '/'
            ? `${u.origin}${u.pathname.replace(/\/$/, '')}`
            : u.origin;
        if (publicBase) {
          params.append('charge_api_base', publicBase);
        }
      } catch {
        /* ignore */
      }
    }

    // Denial Management: iframe on :3154 proxies /api → backend.
    if (portNum === 3154) {
      try {
        const u = new URL(baseUrl.split('?')[0]);
        const publicBase =
          u.pathname && u.pathname !== '/'
            ? `${u.origin}${u.pathname.replace(/\/$/, '')}`
            : u.origin;
        if (publicBase) {
          params.append('denial_api_base', publicBase);
        }
      } catch {
        /* ignore */
      }
    }

    // RCM Compliance: iframe on :3158 proxies /api → backend.
    if (portNum === 3158) {
      try {
        const u = new URL(baseUrl.split('?')[0]);
        const publicBase =
          u.pathname && u.pathname !== '/'
            ? `${u.origin}${u.pathname.replace(/\/$/, '')}`
            : u.origin;
        if (publicBase) {
          params.append('compliance_api_base', publicBase);
        }
      } catch {
        /* ignore */
      }
    }

    // AR Posting: iframe on :3155 proxies /api → backend (same pattern as Denial :3154).
    if (parseInt(urlStr, 10) === 3155) {
      try {
        const u = new URL(baseUrl.split('?')[0]);
        const publicBase =
          u.pathname && u.pathname !== '/'
            ? `${u.origin}${u.pathname.replace(/\/$/, '')}`
            : u.origin;
        if (publicBase) {
          params.append('ar_posting_api_url', publicBase);
        }
      } catch {
        /* ignore */
      }
    }

    // EDI Platform: pass API origin when shell resolves it (remote reverse-proxy / explicit env).
    // EDI's apiConfig.js rejects relative URLs, so absolutize /rcm-api/edi-platform against the shell origin.
    if (portNum === 3010) {
      const apiBase = getServiceApiUrl('EDI_PLATFORM');
      const trimmed = apiBase ? String(apiBase).replace(/\/+$/, '') : '';
      if (trimmed) {
        let absolute = trimmed;
        try {
          absolute = new URL(trimmed, window.location.origin).href.replace(/\/+$/, '');
        } catch {
          /* keep as-is */
        }
        params.append('edi_platform_api_url', absolute);
      }
    }

    // Practice Data Manager: forward module deep-link (X12 pipeline, PHI transformer, etc.).
    if (portNum === 3120) {
      const moduleParam = new URLSearchParams(location.search).get('module');
      if (moduleParam) {
        params.append('module', moduleParam);
      }
    }

    const queryString = params.toString();
    const normalizedPath = pathSuffix && !pathSuffix.startsWith('/') ? `/${pathSuffix}` : pathSuffix;
    return `${baseUrl}${normalizedPath}?${queryString}`;
  }, [serviceUrl, servicePath, iframePath, chromeless, organizationId, organizationRcmSettings, location.search]);

  const frameUrl = getServiceUrl();

  /** Browsers block http:// iframes inside https:// parent pages. */
  const mixedContentBlocked = useMemo(() => {
    if (typeof window === 'undefined' || !frameUrl) return false;
    if (window.location.protocol !== 'https:') return false;
    try {
      return new URL(frameUrl.split('?')[0]).protocol === 'http:';
    } catch {
      return false;
    }
  }, [frameUrl]);

  const {
    status: embedStatus,
    retry: retryEmbed,
    retryKey: embedRetryKey,
  } = useEmbedReachability(frameUrl, { enabled: Boolean(frameUrl) && !mixedContentBlocked });

  // frameUrl is recomputed on every render (it reads localStorage token
  // live) and includes auth_token/org_rcm, which change repeatedly while
  // the shell's OrganizationContext hydrates. Only commit a new iframe
  // src/key when the service identity (base URL + org) actually changes —
  // otherwise pushEmbedContextToIframe's postMessage keeps an already-loaded
  // iframe's auth/org data current without forcing a disruptive reload.
  const frameIdentityKey = `${frameUrl.split('?')[0]}:${organizationId || ''}:${embedRetryKey}`;
  const committedFrameRef = useRef({ key: null, url: frameUrl });
  if (committedFrameRef.current.key !== frameIdentityKey) {
    committedFrameRef.current = { key: frameIdentityKey, url: frameUrl };
  }
  const committedFrameUrl = committedFrameRef.current.url;

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    if (frameUrl) {
      console.log(`[ServiceFrame] Loading ${serviceName} from: ${frameUrl}`);
      console.log(`[ServiceFrame] Organization ID: ${organizationId || 'not available'}`);
    }
    
    // Set a timeout to detect if iframe never loads
    const timeout = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          return false;
        }
        return prev;
      });
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
    // Reset loading only when the iframe actually reloads (frameIdentityKey),
    // not on every frameUrl string change (token/org_rcm churn) — the iframe's
    // src is now frozen to committedFrameUrl for those, so onLoad won't refire
    // and loading would otherwise get stuck true until the 10s failsafe.
  }, [frameIdentityKey, serviceName]);
  
  const pushEmbedContextToIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const org =
      organizationId ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId');
    let rcm = organizationRcmSettings;
    if (!rcm || typeof rcm !== 'object') {
      try {
        const raw = sessionStorage.getItem('organization_rcm_settings');
        if (raw) rcm = JSON.parse(raw);
      } catch {
        /* ignore */
      }
    }
    iframe.contentWindow.postMessage(
      {
        type: 'AUTH_DATA',
        authToken: token ?? undefined,
        refreshToken: refreshToken ?? undefined,
        refresh_token: refreshToken ?? undefined,
        organizationId: org ?? undefined,
        organizationRcmSettings: rcm ?? undefined,
      },
      '*'
    );
    if (org) {
      iframe.contentWindow.postMessage(
        { type: 'ORGANIZATION_CONTEXT', organizationId: org, organizationRcmSettings: rcm ?? undefined },
        '*'
      );
    }
  }, [organizationId, organizationRcmSettings]);

  // Respond to iframe auth requests; also re-push when org changes after load.
  useEffect(() => {
    if (!frameUrl) return;

    const handleMessage = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (
        event.data.type === 'REQUEST_AUTH_DATA' ||
        event.data.type === 'REQUEST_ORG_CONTEXT'
      ) {
        pushEmbedContextToIframe();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [frameUrl, pushEmbedContextToIframe]);

  useEffect(() => {
    if (!loading && frameUrl) {
      pushEmbedContextToIframe();
    }
  }, [organizationId, loading, frameUrl, pushEmbedContextToIframe]);

  const handleLoad = () => {
    console.log(`[ServiceFrame] Successfully loaded ${serviceName}`);
    setLoading(false);
    setError(null);
    pushEmbedContextToIframe();
  };

  const handleError = (e) => {
    setLoading(false);
    setError(`Unable to load ${serviceName || 'service'} in iframe. This service may have security restrictions that prevent iframe embedding.`);
  };

  // Do not run a post-load "contentDocument" check: iframe is cross-origin (different port),
  // so we cannot read contentDocument and would wrongly show "security restrictions".

  if (!frameUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500">Service URL not configured</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full" style={{ height: 'calc(100vh - 64px)' }}>
      {mixedContentBlocked && (
        <div
          className="mx-2 mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
          role="alert"
        >
          <strong className="font-semibold">Cannot embed this app while using HTTPS:</strong> the iframe URL uses{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">http://</code> but AgenticRCM is loaded over{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">https://</code>. Browsers block that (mixed content).
          Serve the embedded app over HTTPS, or set the matching env (e.g.{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">REACT_APP_CHARGE_CAPTURE_FRONTEND_URL</code>,{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">REACT_APP_INTEGRATIONS_FRONTEND_URL</code>) to an HTTPS
          reverse-proxy URL, then rebuild the shell. If the service is only{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">http://</code>, open it in a new tab from the link below
          or use HTTP for the shell during development.
        </div>
      )}
      {(loading || embedStatus === 'checking') && !mixedContentBlocked && embedStatus !== 'unavailable' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading {serviceName || 'service'}...</p>
          </div>
        </div>
      )}

      {embedStatus === 'unavailable' && !mixedContentBlocked && (
        <EmbedUnavailablePanel
          serviceName={serviceName || 'Service'}
          src={frameUrl}
          onRetry={retryEmbed}
        />
      )}
      
      {error && embedStatus === 'ready' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
          <div className="text-center max-w-md">
            <p className="text-red-600 mb-4">{error}</p>
            <a
              href={frameUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Open {serviceName || 'service'} in new tab
            </a>
          </div>
        </div>
      )}

      {embedStatus === 'ready' && !mixedContentBlocked && (
        <iframe
          ref={iframeRef}
          key={frameIdentityKey}
          src={committedFrameUrl}
          className="w-full h-full border-0"
          title={serviceName || 'Service'}
          onLoad={handleLoad}
          onError={handleError}
          allow="fullscreen; clipboard-write; clipboard-read"
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: 'calc(100vh - 64px)',
            display: 'block'
          }}
        />
      )}
    </div>
  );
};

export default ServiceFrame;
